import React, { useState, useEffect, useCallback } from 'react';
import type { Socket } from 'socket.io-client';
import '../styles/ConstructionHotbar.css';

interface ConstructionKit {
  itemId: string;
  name: string;
  icon: string;
}

interface ConstructionHotbarProps {
  socket: Socket | null;
  sceneRef: React.MutableRefObject<any>;
  isConstructionActive: boolean;
  currentKitId: string | null;
}

export const ConstructionHotbar: React.FC<ConstructionHotbarProps> = ({
  socket,
  sceneRef,
  isConstructionActive,
  currentKitId,
}) => {
  const [hotbar, setHotbar] = useState<(ConstructionKit | null)[]>(Array(9).fill(null));
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);

  // Load construction hotbar from server
  useEffect(() => {
    if (!socket) return;

    const loadHotbar = (): void => {
      socket.emit('construction:hotbar:load', (config: ConstructionKit[] | null) => {
        if (config) {
          console.log('[CONSTRUCTION_HOTBAR] Loaded:', config.map(k => k?.name || 'empty'));
          setHotbar(config);
        }
      });
    };

    const handleShipSpawn = (): void => {
      loadHotbar();
    };

    const handleReconnect = (): void => {
      loadHotbar();
    };

    socket.on('ship:spawn', handleShipSpawn);
    socket.on('reconnect', handleReconnect);

    // Listen for hotbar updates from server
    socket.on('construction:hotbar:updated', (config: ConstructionKit[]) => {
      console.log('[CONSTRUCTION_HOTBAR] Server update:', config.map(k => k?.name || 'empty'));
      setHotbar(config);
    });

    // Initial load
    loadHotbar();

    return () => {
      socket.off('ship:spawn', handleShipSpawn);
      socket.off('reconnect', handleReconnect);
      socket.off('construction:hotbar:updated');
    };
  }, [socket]);

   // Handle keyboard shortcuts (1-9)
   const equipConstructionKit = useCallback(
     (slotIndex: number) => {
       const kit = hotbar[slotIndex];
       if (!kit) {
         console.warn(`[CONSTRUCTION_HOTBAR] Slot ${slotIndex + 1} is empty`);
         return;
       }

       setSelectedSlot(slotIndex);
       console.log(`[CONSTRUCTION_HOTBAR] Equipping slot ${slotIndex + 1}: ${kit.name}`);

       // Enter construction mode
       if (sceneRef.current?.enterConstructionMode) {
         sceneRef.current.enterConstructionMode(kit.itemId, kit.name);
       }
     },
     [hotbar, sceneRef]
   );

   useEffect(() => {
     const handleKeyDown = (e: KeyboardEvent) => {
       if (!socket) return;

       const key = e.key;
       if (key >= '1' && key <= '9') {
         const slotIndex = parseInt(key) - 1;
         e.preventDefault();
         equipConstructionKit(slotIndex);
       }

       // ESC to exit construction mode
       if (key === 'Escape' && sceneRef.current?.exitConstructionMode) {
         sceneRef.current.exitConstructionMode();
       }
     };

     window.addEventListener('keydown', handleKeyDown);
     return () => window.removeEventListener('keydown', handleKeyDown);
   }, [socket, sceneRef, equipConstructionKit]);

  if (!socket) return null;

  return (
    <div className="construction-hotbar" style={{ display: isConstructionActive ? 'flex' : 'none' }}>
      <div className="construction-hotbar-label">🔨 CONSTRUCTION HOTBAR</div>
      
      <div className="construction-hotbar-slots">
        {hotbar.map((kit, index) => (
           <button
             key={index}
             className={`construction-hotbar-slot ${selectedSlot === index ? 'active' : ''} ${kit ? 'filled' : 'empty'}`}
             onClick={() => equipConstructionKit(index)}
             title={kit ? `${kit.name} (${index + 1})` : `Slot ${index + 1} (Empty)`}
           >
            <div className="slot-content">
              {kit && (
                <>
                  <div className="slot-icon">🧱</div>
                  <div className="slot-name">{kit.name}</div>
                  <div className="slot-key">{index + 1}</div>
                </>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="construction-hotbar-info">
        <div className="info-text">
          {currentKitId ? `Active: ${hotbar.find(k => k?.itemId === currentKitId)?.name}` : 'No kit selected'}
        </div>
        <div className="info-hint">Press 1-9 to equip | ESC to exit</div>
      </div>
    </div>
  );
};
